import React from "react";

interface SparklineProps {
  values: number[];
  label: string;
  /** Trend direction drives the ink: the accent when it is going the right way, muted when it is not. */
  tone?: "accent" | "muted";
  className?: string;
}

// Full-bleed under the value so the trend reads as the number's shape; dropped below sm, where it is unreadable line
const VIEW_W = 200;
const VIEW_H = 40;
const STROKE = 2;

const Sparkline: React.FC<SparklineProps> = ({ values, label, tone = "accent", className = "" }) => {
  // An all-zero series has no shape to show; a flat line at the floor reads as a broken chart
  if (values.length < 2 || values.every((value) => value === 0)) return null;

  const max = Math.max(...values);
  const min = Math.min(...values);
  const span = max - min || 1;
  const step = VIEW_W / (values.length - 1);

  const points = values.map((value, index) => {
    const x = index * step;
    const y = VIEW_H - STROKE / 2 - ((value - min) / span) * (VIEW_H - STROKE);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });

  return (
    <svg
      className={`hidden h-8 w-full sm:block ${
        tone === "accent" ? "text-eai-cta-primary" : "text-eai-slash"
      } ${className}`}
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      preserveAspectRatio="none"
      fill="none"
      role="img"
      aria-label={`${label}: ${values.join(", ")}`}
    >
      <title>{`${label} — low ${min}, high ${max}`}</title>
      <polyline
        points={points.join(" ")}
        stroke="currentColor"
        strokeWidth={STROKE}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

export default Sparkline;
